import { FilterList } from "@mui/icons-material";
import { Badge, Button, Menu, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useState } from "react";
import { TaskItemsFilterToggleButton, TaskItemsSortToggleButton } from "../../../../components";
import {
  SortByDirection,
  TaskItemsFilterByOption,
  TaskItemStatus,
  TaskItemsSortByOption,
  UserTaskItemsGroupByOption,
} from "../../../../constants";
import { useUserImplementationSettings } from "../hooks";

interface Props {
  userGuid: string;
  disabled?: boolean;
}

const UserTaskItemsFilterButton = (props: Props) => {
  const { settings, updateSettings } = useUserImplementationSettings(props.userGuid);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  function handleGroupByChanged(value: UserTaskItemsGroupByOption) {
    if (value != null) {
      updateSettings({
        taskItemsGroupByOption: value,
      });
    }
  }

  function handleFilterByChanged(
    filterByOption: TaskItemsFilterByOption,
    filterByStatusOption: TaskItemStatus | undefined
  ) {
    updateSettings({
      taskItemsFilterByOption: filterByOption,
      taskItemsFilterByStatusOption: filterByStatusOption,
    });
  }

  function handleSortByChanged(value: TaskItemsSortByOption) {
    updateSettings({
      taskItemsSortByOption: value,
    });
  }

  function handleSortDirectionChanged(value: SortByDirection) {
    updateSettings({
      taskItemsSortByDirection: value,
    });
  }

  return (
    <>
      <Badge color="primary" variant="dot" invisible={settings.taskItemsFilterByOption === TaskItemsFilterByOption.All}>
        <Button
          variant="tertiary"
          disabled={props.disabled}
          sx={{ minWidth: 0, p: 0.25 }}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <FilterList />
        </Button>
      </Badge>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
        anchorOrigin={{
          horizontal: "right",
          vertical: "bottom",
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: "top",
        }}
      >
        <Typography variant="overline" sx={{ mx: 0.5 }}>
          Group By
        </Typography>
        <ToggleButtonGroup
          value={settings.taskItemsGroupByOption}
          exclusive
          orientation="vertical"
          variant="menu"
          onChange={(_, value) => handleGroupByChanged(value)}
          sx={{ minWidth: "100%" }}
        >
          <ToggleButton value={UserTaskItemsGroupByOption.None}>
            <Typography>None</Typography>
          </ToggleButton>
          <ToggleButton value={UserTaskItemsGroupByOption.DueDate}>
            <Typography>Due Date</Typography>
          </ToggleButton>
          <ToggleButton value={UserTaskItemsGroupByOption.InFocus}>
            <Typography>In Focus</Typography>
          </ToggleButton>
          <ToggleButton value={UserTaskItemsGroupByOption.Owner}>
            <Typography>Owner</Typography>
          </ToggleButton>
          <ToggleButton value={UserTaskItemsGroupByOption.PrimaryOwner}>
            <Typography>Primary Owner</Typography>
          </ToggleButton>
          <ToggleButton value={UserTaskItemsGroupByOption.Status}>
            <Typography>Status</Typography>
          </ToggleButton>
          <ToggleButton value={UserTaskItemsGroupByOption.Team}>
            <Typography>Team</Typography>
          </ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="overline" sx={{ mx: 0.5, mt: 1 }}>
          Filter By
        </Typography>
        <TaskItemsFilterToggleButton
          filterByOption={settings.taskItemsFilterByOption}
          filterByStatusOption={settings.taskItemsFilterByStatusOption}
          onChange={handleFilterByChanged}
        />

        <Typography variant="overline" sx={{ mx: 0.5, mt: 1 }}>
          Sort By
        </Typography>
        <TaskItemsSortToggleButton
          sortByOption={settings.taskItemsSortByOption}
          sortByDirection={settings.taskItemsSortByDirection}
          disabledOptions={{
            [TaskItemsSortByOption.Owner]:
              settings.taskItemsGroupByOption === UserTaskItemsGroupByOption.Owner ||
              settings.taskItemsGroupByOption === UserTaskItemsGroupByOption.PrimaryOwner,
          }}
          onSortByChanged={handleSortByChanged}
          onSortDirectionChanged={handleSortDirectionChanged}
        />
      </Menu>
    </>
  );
};

export { UserTaskItemsFilterButton };
